import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X } from 'lucide-react';

interface OnboardingData {
  accountType: 'personal' | 'business';
  companyName?: string;
  cnpj?: string;
  segment?: string;
  teamSize?: string;
  aiKnowledge: 'low' | 'medium' | 'high' | 'expert';
}

interface OnboardingModalProps {
  userName: string;
  onComplete: (data: OnboardingData) => void;
  onSkip?: () => void;
}

export function OnboardingModal({ userName, onComplete, onSkip }: OnboardingModalProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<OnboardingData>({
    accountType: 'personal',
    aiKnowledge: 'medium'
  });

  const segments = [
    { id: 'food', name: 'Alimentos', icon: '🍔' },
    { id: 'health', name: 'Saúde', icon: '⚕️' },
    { id: 'tech', name: 'Tecnologia', icon: '💻' },
    { id: 'education', name: 'Educação', icon: '📚' },
    { id: 'industry', name: 'Indústria', icon: '🏭' },
    { id: 'mei', name: 'MEI', icon: '👤' },
    { id: 'other', name: 'Outro', icon: '🔧' }
  ];

  const teamSizes = [
    { id: 'solo', name: 'Só eu', icon: '👤' },
    { id: 'small', name: '1 a 10', icon: '👥' },
    { id: 'medium', name: '10 a 100', icon: '👨‍👩‍👧‍👦' },
    { id: 'large', name: '100+', icon: '🏢' }
  ];

  const aiKnowledgeLevels = [
    { id: 'low', name: 'Baixo', description: 'Estou começando agora', icon: '🌱' },
    { id: 'medium', name: 'Médio', description: 'Já usei algumas vezes', icon: '🌿' },
    { id: 'high', name: 'Alto', description: 'Uso regularmente', icon: '🌳' },
    { id: 'expert', name: 'Expert', description: 'Domino as ferramentas', icon: '🚀' }
  ];

  const handleNext = () => {
    if (step === 1) {
      if (formData.accountType === 'personal') {
        setStep(3); // Pula para step 3 (conhecimento de IA)
      } else {
        setStep(2); // Vai para dados da empresa
      }
    } else if (step === 2) {
      setStep(3); // Vai para conhecimento de IA
    }
  };

  const handleComplete = () => {
    onComplete(formData);
  };

  const isStep1Valid = formData.accountType !== undefined;
  const isStep2Valid = formData.accountType === 'personal' || (
    formData.companyName?.trim() &&
    formData.cnpj?.trim() &&
    formData.segment &&
    formData.teamSize
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Olá, {userName}! 👋
            </h2>
            <p className="text-gray-600 mt-1">
              Vamos personalizar sua experiência
            </p>
          </div>
          {onSkip && (
            <button
              onClick={onSkip}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-6">
          <div className="flex items-center justify-center space-x-2">
            <div className={`h-2 w-24 rounded-full transition-colors ${step >= 1 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div className={`h-2 w-24 rounded-full transition-colors ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div className={`h-2 w-24 rounded-full transition-colors ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} />
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">
            Etapa {step} de 3
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Tipo de Conta */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Tipo de acesso
                </h3>
                <p className="text-gray-600">
                  Como você pretende usar o Space AI?
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setFormData({ ...formData, accountType: 'personal' })}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    formData.accountType === 'personal'
                      ? 'border-blue-600 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-4xl mb-3">👤</div>
                  <h4 className="font-semibold text-lg mb-1">Usuário</h4>
                  <p className="text-sm text-gray-600">
                    Para uso pessoal e individual
                  </p>
                </button>

                <button
                  onClick={() => setFormData({ ...formData, accountType: 'business' })}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    formData.accountType === 'business'
                      ? 'border-blue-600 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-4xl mb-3">🏢</div>
                  <h4 className="font-semibold text-lg mb-1">Empresa</h4>
                  <p className="text-sm text-gray-600">
                    Para equipes e organizações
                  </p>
                </button>
              </div>

              <Button
                onClick={handleNext}
                disabled={!isStep1Valid}
                className="w-full"
              >
                Continuar
              </Button>
            </div>
          )}

          {/* Step 2: Dados da Empresa */}
          {step === 2 && formData.accountType === 'business' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Dados da empresa
                </h3>
                <p className="text-gray-600">
                  Conte-nos mais sobre sua organização
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="companyName">Nome da Empresa *</Label>
                  <Input
                    id="companyName"
                    type="text"
                    placeholder="Digite o nome da empresa"
                    value={formData.companyName || ''}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <Input
                    id="cnpj"
                    type="text"
                    placeholder="00.000.000/0000-00"
                    value={formData.cnpj || ''}
                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Segmento *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                    {segments.map((segment) => (
                      <button
                        key={segment.id}
                        onClick={() => setFormData({ ...formData, segment: segment.id })}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          formData.segment === segment.id
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">{segment.icon}</div>
                        <div className="text-sm font-medium">{segment.name}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Quantidade de colaboradores *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                    {teamSizes.map((size) => (
                      <button
                        key={size.id}
                        onClick={() => setFormData({ ...formData, teamSize: size.id })}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData.teamSize === size.id
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">{size.icon}</div>
                        <div className="text-sm font-medium">{size.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1"
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!isStep2Valid}
                  className="flex-1"
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Conhecimento de IA */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Nível de conhecimento em IA
                </h3>
                <p className="text-gray-600">
                  Isso nos ajuda a personalizar sua experiência
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiKnowledgeLevels.map((level) => (
                  <button
                    key={level.id}
                    onClick={() => setFormData({ ...formData, aiKnowledge: level.id as any })}
                    className={`p-6 rounded-lg border-2 transition-all text-left ${
                      formData.aiKnowledge === level.id
                        ? 'border-blue-600 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-4xl mb-3">{level.icon}</div>
                    <h4 className="font-semibold text-lg mb-1">{level.name}</h4>
                    <p className="text-sm text-gray-600">{level.description}</p>
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(formData.accountType === 'personal' ? 1 : 2)}
                  variant="outline"
                  className="flex-1"
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleComplete}
                  className="flex-1"
                >
                  Começar 🚀
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Skip Option */}
        {onSkip && (
          <div className="text-center px-6 pb-6">
            <button
              onClick={onSkip}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Pular por enquanto →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

